import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import client from "../lib/api";
import { ArrowRight, Lock, TrendingUp, Activity, Trophy, Send, Share2 } from "lucide-react";
import { ShareTradeModal } from "../components/ShareTradeModal";

const TELEGRAM_HANDLE = "TFTDubai";
const RANGES = [["today", "Today"], ["week", "This Week"], ["month", "This Month"], ["all", "All-Time"]];

const fmtPips = (v) => `${v >= 0 ? "+" : ""}${v}`;
const relTime = (iso) => {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 60000;
  if (diff < 1) return "just now";
  if (diff < 60) return `${Math.floor(diff)}m ago`;
  if (diff < 1440) return `${Math.floor(diff / 60)}h ago`;
  return `${Math.floor(diff / 1440)}d ago`;
};

const StatBox = ({ label, value, accent, testid }) => (
  <div className="surface px-4 py-3 text-center" data-testid={testid}>
    <div className={`font-head text-2xl md:text-3xl font-bold ${accent}`}>{value}</div>
    <div className="text-[10px] uppercase tracking-wider text-slate-400 mt-1">{label}</div>
  </div>
);

export default function LiveResults() {
  const [data, setData] = useState(null);
  const [range, setRange] = useState("today");
  const [share, setShare] = useState(null);

  useEffect(() => {
    const load = () => client.get("/public/trades").then(({ data }) => setData(data)).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const r = data?.ranges?.[range];

  return (
    <div className="min-h-screen grain" style={{ background: "var(--app-bg)" }}>
      <header className="h-16 glass sticky top-0 z-20 flex items-center px-6 max-w-7xl mx-auto w-full">
        <Link to="/" className="flex items-center gap-2" data-testid="results-home-link">
          <div className="w-8 h-8 bg-gold text-black flex items-center justify-center font-head font-bold">F</div>
          <span className="font-head font-bold text-white">Fateh FX</span>
        </Link>
        <Link to="/open-account" className="ml-auto btn-gold px-4 py-2 text-sm flex items-center gap-1" data-testid="results-cta-top">Open Account <ArrowRight size={14} /></Link>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 pt-12 pb-6">
        <div className="inline-flex items-center gap-2 px-3 py-1 border border-gold text-gold text-xs font-mono uppercase tracking-widest mb-5">
          <span className="live-dot w-1.5 h-1.5 rounded-full bg-gold" />Live VIP Signal Results
        </div>
        <h1 className="font-head text-4xl md:text-5xl font-bold text-white tracking-tight max-w-3xl leading-[1.08]">
          Real Gold & Bitcoin signal <span className="text-gold">results</span>, updated live
        </h1>
        <p className="text-slate-400 mt-4 max-w-2xl text-base">
          Every closed trade, win and loss — tracked straight from our VIP desk. Running signals show live, entries stay locked for members.
        </p>
      </section>

      {/* Running signals */}
      <section className="max-w-7xl mx-auto px-6 pb-4" data-testid="running-section">
        <div className="flex items-center gap-2 mb-3">
          <Activity size={16} className="text-emerald-400" />
          <h2 className="font-head text-lg font-semibold text-white">Running Now</h2>
          {data && <span className="text-xs text-slate-500">{data.running_count} live</span>}
        </div>
        {data && data.running.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {data.running.map((t, i) => (
              <div key={i} className="surface p-4 border-l-2 border-emerald-500" data-testid={`running-${i}`}>
                <div className="flex items-center justify-between">
                  <span className="font-head font-semibold text-white">{t.icon} {t.instrument}</span>
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-emerald-400"><span className="live-dot w-1.5 h-1.5 rounded-full bg-emerald-400" />Running</span>
                </div>
                <div className={`font-head text-2xl font-bold mt-2 ${(t.running_pips || 0) >= 0 ? "text-emerald-400" : "text-red-400"}`}>{fmtPips(t.running_pips || 0)} <span className="text-xs text-slate-500 font-sans font-normal">pips</span></div>
                <div className="flex items-center gap-1 text-[11px] text-slate-500 mt-1"><Lock size={11} /> Entry locked · members only</div>
              </div>
            ))}
          </div>
        ) : (
          <div className="surface p-5 text-sm text-slate-500">No signals running right now — new setups drop through the trading day.</div>
        )}
      </section>

      {/* Range tabs + stats */}
      <section className="max-w-7xl mx-auto px-6 py-6">
        <div className="grid grid-cols-4 sm:flex border border-[color:var(--border-subtle)] sm:w-fit mb-6">
          {RANGES.map(([k, label]) => (
            <button key={k} onClick={() => setRange(k)} data-testid={`range-${k}`}
              className={`px-2 sm:px-4 py-2.5 sm:py-2 text-xs sm:text-sm whitespace-nowrap transition-colors duration-200 ${range === k ? "bg-gold text-black font-semibold" : "text-slate-400 hover:text-white"}`}>{label}</button>
          ))}
        </div>

        {r ? (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="range-stats">
              <StatBox testid="stat-winrate" label="Win Rate" value={`${r.win_rate}%`} accent="text-gold" />
              <StatBox testid="stat-net" label="Net Pips" value={fmtPips(r.net_pips)} accent={r.net_pips >= 0 ? "text-emerald-400" : "text-red-400"} />
              <StatBox testid="stat-closed" label="Closed Trades" value={r.count} accent="text-white" />
              <StatBox testid="stat-wl" label="Win / Loss" value={`${r.wins}/${r.losses}`} accent="text-white" />
            </div>

            {r.trades.length > 0 ? (
              <div className="surface divide-y divide-[color:var(--border-subtle)]" data-testid="closed-list">
                {r.trades.map((t, i) => (
                  <div key={i} className="flex items-center gap-3 px-4 py-3" data-testid={`closed-${i}`}>
                    <span className="text-lg">{t.icon}</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-white font-head font-semibold">{t.instrument}{t.direction ? ` · ${t.direction}` : ""}</div>
                      <div className="text-[11px] text-slate-500">{t.closed_date} · {relTime(t.closed_at)}</div>
                    </div>
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 border ${t.outcome === "win" ? "text-emerald-400 border-emerald-500/40" : "text-red-400 border-red-500/40"}`}>{t.outcome === "win" ? "Win" : "Loss"}</span>
                    <span className={`font-head font-bold text-lg w-20 text-right ${(t.net_pips || 0) >= 0 ? "text-emerald-400" : "text-red-400"}`}>{fmtPips(t.net_pips)}</span>
                    <button onClick={() => setShare(t)} data-testid={`share-${i}`} title="Share this result" className="text-slate-500 hover:text-gold transition-colors duration-200 p-1"><Share2 size={16} /></button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="surface p-6 text-sm text-slate-500 flex items-center gap-2"><TrendingUp size={16} className="text-gold" /> No closed trades in this period yet — check the running signals above.</div>
            )}
          </>
        ) : (
          <div className="text-sm text-slate-500">Loading results…</div>
        )}
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 pb-16">
        <div className="glass border border-gold/40 p-6 md:p-8 flex flex-col md:flex-row items-center gap-5">
          <Trophy size={32} className="text-gold shrink-0" />
          <div className="flex-1 text-center md:text-left">
            <h3 className="font-head text-xl font-semibold text-white">Want these signals live — with full entry, SL & targets?</h3>
            <p className="text-sm text-slate-400 mt-1">Open an account with our partner broker, send your screenshot to @{TELEGRAM_HANDLE}, and unlock the VIP desk free.</p>
          </div>
          <div className="flex gap-3 shrink-0">
            <Link to="/open-account" className="btn-gold px-5 py-2.5 text-sm flex items-center gap-2" data-testid="results-cta-bottom">Open Account <ArrowRight size={15} /></Link>
            <a href={`https://t.me/${TELEGRAM_HANDLE}`} target="_blank" rel="noreferrer" className="px-5 py-2.5 text-sm flex items-center gap-2 border border-gold text-gold hover:bg-[rgba(212,175,55,0.1)] transition-colors duration-200"><Send size={15} />Telegram</a>
          </div>
        </div>
      </section>

      <footer className="border-t border-[color:var(--border-subtle)] py-8 text-center text-xs text-slate-600">
        Results reflect model VIP signal tracking · Actual client results vary with spread, slippage & position size · Trading involves risk, no guaranteed profits
      </footer>
      <ShareTradeModal trade={share} onClose={() => setShare(null)} />
    </div>
  );
}
